import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import Select from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Home, Search, Trash2, Pencil, CheckCircle, RefreshCw, X, Calendar, DollarSign, User, ExternalLink, Activity
} from 'lucide-react';
import { getHomestays, updateHomestayStatus, deleteHomestay, getHomestayBookings, updateHomestayBookingStatus } from '../../lib/apiService';
import paperIcon from '../../assets/paper.png';

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
  inactive: 'bg-slate-100 text-slate-600 border border-slate-300',
};

const HomestaysPage = () => {
  const [homestays, setHomestays] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modals / State
  const [deleteModal, setDeleteModal] = useState(null);
  const [bookingsModal, setBookingsModal] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingPages, setBookingPages] = useState(1);

  const fetchList = useCallback(async (pageNum = 1, searchQuery = search) => {
    setLoading(true);
    try {
      const res = await getHomestays({
        page: pageNum,
        limit: 20,
        search: searchQuery,
      });
      if (res.status) {
        setHomestays(res.homestays || []);
        setTotal(res.total || 0);
        setPage(res.page || 1);
        setPages(res.pages || 1);
      } else {
        toast.error(res.message || 'Failed to fetch homestays');
      }
    } catch (error) {
      toast.error('Error fetching homestays');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchList(1, search);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const res = await updateHomestayStatus(id, newStatus);
      if (res.status) {
        toast.success(res.message);
        setHomestays(prev => prev.map(h => h.id === id ? { ...h, status: newStatus } : h));
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      const res = await deleteHomestay(deleteModal.id);
      if (res.status) {
        toast.success(res.message);
        setHomestays(prev => prev.filter(h => h.id !== deleteModal.id));
        setDeleteModal(null);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error('Error deleting homestay');
    }
  };

  const openBookings = async (hs) => {
    setBookingsModal(hs);
    setBookingPage(1);
    fetchBookings(hs.id, 1);
  };

  const fetchBookings = async (id, pageNum) => {
    setBookingsLoading(true);
    try {
      const res = await getHomestayBookings(id, pageNum);
      if (res.status) {
        setBookings(res.bookings || []);
        setBookingPage(res.page || 1);
        setBookingPages(res.pages || 1);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Failed to fetch bookings');
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      const res = await updateHomestayBookingStatus(bookingId, newStatus);
      if (res.status) {
        toast.success(res.message);
        setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error updating booking status');
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-[1600px] mx-auto min-h-screen bg-slate-50/50">
      <Helmet>
        <title>Homestays | Admin</title>
      </Helmet>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              Homestays Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage all homestays. Total: {total}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchList(page)}
              className="bg-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="mb-6 flex flex-col md:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, owner, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
          <Button type="submit" variant="secondary">Search</Button>
        </form>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : homestays.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-16 px-4 bg-white/50 backdrop-blur-sm border-dashed">
            <img src={paperIcon} alt="Empty" className="w-[150px] h-[150px] object-contain mb-4 opacity-70" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No Homestays Found</h3>
            <p className="text-slate-500 text-center max-w-sm mb-6">
              Try adjusting your search criteria.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {homestays.map((hs) => (
              <Card key={hs.id} className="group overflow-hidden bg-white hover:shadow-lg transition-all duration-300 border-slate-200">
                <div className="relative h-48 bg-slate-100">
                  <div className="absolute top-3 left-3 z-10">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm backdrop-blur-md ${STATUS_BADGE[hs.status] || STATUS_BADGE.inactive}`}>
                      {hs.status.toUpperCase()}
                    </span>
                  </div>

                  {hs.images && hs.images.length > 0 ? (
                    <img
                      src={hs.images[0]}
                      alt={hs.homestay_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://placehold.co/600x400?text=No+Image' }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <Home className="w-12 h-12 opacity-20" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <h3 className="text-lg font-bold line-clamp-1">{hs.homestay_name}</h3>
                    <p className="text-sm text-slate-200 truncate">{hs.city}, {hs.state}</p>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">Owner</p>
                      <p className="text-slate-900 font-medium">{hs.owner_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 font-medium">Views</p>
                      <p className="text-slate-900 font-medium">{hs.views_count}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`text-xs ${hs.status === 'active' ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'}`}
                        onClick={() => handleToggleStatus(hs.id, hs.status)}
                      >
                        {hs.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-8 px-2"
                        onClick={() => openBookings(hs)}
                        title="View Bookings"
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Bookings
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                        onClick={() => setDeleteModal(hs)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between pt-6 border-t border-slate-200 mt-8">
            <p className="text-sm text-slate-500">
              Showing page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => fetchList(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pages}
                onClick={() => fetchList(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <Dialog open={!!deleteModal} onOpenChange={() => setDeleteModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Homestay
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteModal?.homestay_name}</strong>?
              This action cannot be undone and will delete all associated rooms, photos, and bookings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setDeleteModal(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Yes, Delete Homestay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bookings Modal */}
      <Dialog open={!!bookingsModal} onOpenChange={() => setBookingsModal(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Bookings for {bookingsModal?.homestay_name}
            </DialogTitle>
            <DialogDescription>
              Manage all bookings associated with this homestay.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {bookingsLoading ? (
              <div className="flex justify-center p-8"><Activity className="w-6 h-6 animate-spin text-indigo-600" /></div>
            ) : bookings.length === 0 ? (
              <div className="text-center p-8 text-slate-500">No bookings found for this homestay.</div>
            ) : (
              <div className="space-y-4">
                {bookings.map(b => (
                  <Card key={b.id} className="p-4 bg-slate-50 border border-slate-200">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                          {b.guest_name || b.guest_user_name || 'Guest'}
                          <span className="text-sm font-normal text-slate-500">({b.guest_phone || b.guest_user_phone})</span>
                        </h4>
                        <p className="text-sm text-slate-600 mt-1">Room: <span className="font-medium text-slate-800">{b.room_label || `Room #${b.room_id}`}</span></p>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Check-in: {new Date(b.check_in_date).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Check-out: {new Date(b.check_out_date).toLocaleDateString()}</span>
                          <span>Guests: {b.num_guests}</span>
                        </div>
                        <div className="mt-2 text-sm font-medium text-slate-900 flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-emerald-600" /> Total: ₹{b.total_price}
                          <span className="text-slate-400 ml-2">|</span>
                          <span className="text-indigo-600 ml-2">Fee: ₹{b.booking_fee}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0 min-w-[140px]">
                        <Select
                          value={b.status}
                          onChange={(val) => handleUpdateBookingStatus(b.id, val)}
                          options={[
                            { value: 'pending', label: 'Pending' },
                            { value: 'approved', label: 'Approved' },
                            { value: 'rejected', label: 'Rejected' },
                            { value: 'cancelled', label: 'Cancelled' },
                            { value: 'completed', label: 'Completed' },
                            { value: 'refunded', label: 'Refunded' }
                          ]}
                          size="sm"
                          placeholder="Status"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Pagination inside Modal */}
            {bookingPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <Button size="sm" variant="outline" disabled={bookingPage <= 1} onClick={() => fetchBookings(bookingsModal.id, bookingPage - 1)}>Prev</Button>
                <span className="text-sm text-slate-500">Page {bookingPage} of {bookingPages}</span>
                <Button size="sm" variant="outline" disabled={bookingPage >= bookingPages} onClick={() => fetchBookings(bookingsModal.id, bookingPage + 1)}>Next</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomestaysPage;
